/**
 * Question Cache — offline-capable answer-key bundle per game.
 *
 * Why this exists
 * ---------------
 * Schools where SUPERTMATIK is used during competitions often have flaky
 * Wi-Fi networks (30+ kids on a single AP). The legacy answer flow makes
 * one POST to `/play/answer/{game_id}` per round to validate the answer —
 * if that single request times out, the game freezes mid-round and the
 * student can't continue, even though the entire question set is already
 * loaded on the client.
 *
 * This module pre-fetches the per-game answer key (just `{id, position,
 * correct}` triplets) at game-start time and caches it in sessionStorage.
 * All subsequent answer validations resolve LOCALLY in microseconds with
 * zero network dependency. The original network endpoint stays as a
 * fallback for the (rare) case where the cache prefetch failed.
 *
 * Security trade-off
 * ------------------
 * The questions themselves are already returned in cleartext by
 * `/play/start`. Adding the `correct` field is a marginal additional
 * disclosure — a determined cheater could already capture answers from
 * the existing payload. Given that the alternative (game freezing on
 * unstable Wi-Fi during a prize-awarding competition) destroys the user
 * experience, the trade-off is acceptable. Schools relying on integrity
 * already trust teachers to supervise the rooms.
 *
 * Storage shape (per-tab — sessionStorage):
 *   key:   `supert_qcache_${gameId}`
 *   value: { fetchedAt: ms, answer_key: [{id, position, correct, language}] }
 *
 * Public API:
 *   - prefetch(gameId): start the fetch in the background (idempotent)
 *   - resolve({gameId, questionId, position, answerIndex}): returns
 *     `{correct, correct_index, source}` using local cache, else falls
 *     back to the network endpoint.
 *   - clear(gameId?): drop a single game's bundle or the whole cache.
 */

import { api } from "@/lib/api";

const KEY_PREFIX = "supert_qcache_";
const inflight = new Map(); // gameId -> Promise

function readBundle(gameId) {
  try {
    const raw = window.sessionStorage.getItem(KEY_PREFIX + gameId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.answer_key) ? parsed : null;
  } catch {
    return null;
  }
}

function writeBundle(gameId, bundle) {
  try {
    window.sessionStorage.setItem(KEY_PREFIX + gameId, JSON.stringify(bundle));
  } catch {
    /* quota exceeded / private mode — silently degrade */
  }
}

/**
 * Kick off a prefetch of the game's answer key. Idempotent — concurrent
 * calls return the same in-flight Promise. Returns the cached or freshly
 * fetched bundle (or null if both attempts failed; the resolver then
 * falls back to the network on each answer).
 */
export async function prefetch(gameId) {
  if (!gameId) return null;
  const cached = readBundle(gameId);
  if (cached && cached.answer_key.length > 0) return cached;
  if (inflight.has(gameId)) return inflight.get(gameId);

  const promise = (async () => {
    try {
      const res = await api.get(`/play/answer-key/${gameId}`);
      const bundle = {
        fetchedAt: Date.now(),
        answer_key: res.data?.answer_key || [],
      };
      if (bundle.answer_key.length > 0) writeBundle(gameId, bundle);
      return bundle;
    } catch (err) {
      // Don't blow up — the legacy network path still works as a fallback.
      console.warn("[questionCache] prefetch failed for game", gameId, err?.message);
      return null;
    } finally {
      inflight.delete(gameId);
    }
  })();
  inflight.set(gameId, promise);
  return promise;
}

function lookup(bundle, { questionId, position }) {
  if (!bundle) return null;
  const key = bundle.answer_key;
  if (questionId) {
    const hit = key.find((q) => q.id === questionId);
    if (hit) return hit;
    // The questionId was supplied but no matching answer-key entry was
    // found. This can happen when the bundle is stale after a re-import
    // (e.g. new pack uploaded after `/play/start` seeded the cache) — in
    // that case DO NOT silently fall back to a same-position match because
    // that would return a `correct` index for a DIFFERENT question and
    // wrongly mark valid answers as incorrect (Fev 2026 bug report:
    // correct=4/NENHUMA OPÇÃO shown as wrong). Force a network round-trip
    // instead so the server truth wins.
    return null;
  }
  if (position) {
    // Legacy fallback only when no id is supplied (should not happen in
    // modern flows). Server-side /play/answer does the same.
    const hit = key.find((q) => q.position === position);
    if (hit) return hit;
  }
  return null;
}

/**
 * Resolve a single answer locally (cache hit) or via the network (cache
 * miss / no bundle). Same response shape as the legacy
 * `/play/answer/{gameId}` endpoint, plus a `source` field for diagnostics.
 *
 * Args:
 *   gameId        : string
 *   sessionId     : string (only forwarded on network fallback)
 *   questionId    : string | undefined
 *   position      : string | undefined
 *   answerIndex   : 1..4
 *   elapsedSeconds: number (forwarded on network fallback only)
 */
export async function resolve({
  gameId,
  sessionId,
  questionId,
  position,
  answerIndex,
  elapsedSeconds,
}) {
  // Try local first
  let bundle = readBundle(gameId);
  let hit = lookup(bundle, { questionId, position });
  // First answers after a cold page load: the prefetch may STILL be in
  // flight. Await it (instead of firing a duplicate per-answer network
  // round-trip) so the very first round resolves locally too.
  if (!(hit && Number.isInteger(hit.correct)) && inflight.has(gameId)) {
    try {
      const fresh = await inflight.get(gameId);
      if (fresh && Array.isArray(fresh.answer_key)) {
        bundle = fresh;
        hit = lookup(bundle, { questionId, position });
      }
    } catch { /* fall through to network */ }
  }
  if (hit && Number.isInteger(hit.correct)) {
    const correctIdx = Number(hit.correct);
    return {
      correct: correctIdx === Number(answerIndex),
      correct_index: correctIdx,
      source: "local",
    };
  }

  // Cache miss — fall back to network and (if possible) backfill cache
  try {
    const res = await api.post(`/play/answer/${gameId}`, {
      session_id: sessionId,
      position,
      question_id: questionId,
      answer_index: answerIndex,
      elapsed_seconds: elapsedSeconds,
    });
    // Backfill cache for this specific question so subsequent answers in the
    // same session don't repeat the network round-trip even if the prefetch
    // failed earlier.
    if (bundle && questionId) {
      bundle.answer_key.push({
        id: questionId,
        position,
        correct: Number(res.data?.correct_index || 0),
        language: "pt",
      });
      writeBundle(gameId, bundle);
    }
    return { ...res.data, source: "network" };
  } catch (err) {
    // Network truly unavailable. Trigger a background prefetch attempt so
    // the next answer may hit the cache, and bubble the error so the caller
    // can decide what to do (typically: optimistic UI + retry).
    prefetch(gameId);
    throw err;
  }
}

/** Drop one game's cache or all of them. */
export function clear(gameId) {
  if (gameId) {
    window.sessionStorage.removeItem(KEY_PREFIX + gameId);
    return;
  }
  for (let i = window.sessionStorage.length - 1; i >= 0; i--) {
    const k = window.sessionStorage.key(i);
    if (k && k.startsWith(KEY_PREFIX)) window.sessionStorage.removeItem(k);
  }
}

/** Diagnostic helper — returns the number of pre-cached questions for a
 *  game. Used by the OutboxStatusBadge to show "Offline ready" indicator. */
export function cachedCount(gameId) {
  const bundle = readBundle(gameId);
  return bundle?.answer_key?.length || 0;
}

/**
 * Peek at the correct-answer index for a given question without submitting
 * an answer. Used by the DEMO tutorial's auto-play robot so it can click
 * the correct option without going through the resolver.
 *
 * Returns the 1..4 index when available, or null otherwise (cache miss).
 */
export function peekCorrectIndex(gameId, { questionId, position } = {}) {
  const bundle = readBundle(gameId);
  const hit = lookup(bundle, { questionId, position });
  if (hit && Number.isInteger(hit.correct)) return Number(hit.correct);
  return null;
}
